import { classNames } from "helpers/commonHelpers";
// eslint-disable-next-line import/no-cycle
import { GroupedTrackingHistoryItem } from "./GroupedTrackingHistory";
import { TrackingHistoryItemProps } from "../TrackingHistoryItem/TrackingHistoryItem";

/**
 * Checks if a date is completed (in the past)
 * @param date - Date to check if it is completed
 * @returns {boolean} - true if the date is completed, false otherwise
 */
const isCompleted = (date: string) => new Date(date) < new Date();

/**
 * Get the classes for the line of the grouped tracking history item
 * @param index - index of the grouped tracking history item
 * @param trackingHistoryItems - array of tracking history items
 * @param history - array of grouped tracking history items
 * @param activeAccordion - the active accordion
 * @returns {string} - the classes for the line of the grouped tracking history item
 */
const getLineClasses = (
  index: number,
  trackingHistoryItems: TrackingHistoryItemProps[],
  history: GroupedTrackingHistoryItem[],
  activeAccordion: string | null,
) => {
  const isGroupCompleted =
    isCompleted(trackingHistoryItems[trackingHistoryItems.length - 1].date) &&
    !trackingHistoryItems[trackingHistoryItems.length - 1].isEstimatedDate;
  const isFirstItemCompleted = isCompleted(trackingHistoryItems[0].date) && !trackingHistoryItems[0].isEstimatedDate;
  const isLastGroup = index === history.length - 1;
  const isExpanded = activeAccordion === history[index].groupName + index;
  const { groupSubtitle } = history[index];

  return classNames("grouped-tracking-history__line", {
    "grouped-tracking-history__line--hidden": isLastGroup && !isExpanded,
    "grouped-tracking-history__line--expanded": isExpanded,
    "grouped-tracking-history__line--has-subtitle": groupSubtitle,
    "grouped-tracking-history__line--completed": (isFirstItemCompleted && isExpanded) || isGroupCompleted,
    "grouped-tracking-history__line--incompleted": !isFirstItemCompleted,
    "grouped-tracking-history__line--upcoming":
      isGroupCompleted &&
      (!isCompleted(history[index + 1]?.history[0]?.date) || history[index + 1]?.history?.[0]?.isEstimatedDate) &&
      !isExpanded,
  });
};

/**
 * Get the classes for the reverse line of the grouped tracking history item
 * @param index - index of the grouped tracking history item
 * @param history - array of grouped tracking history items
 * @param activeAccordion - the active accordion
 * @param groupSubtitle - the subtitle of the group
 * @returns {string} - the classes for the reverse line of the grouped tracking history item
 */
const getReverseLineClasses = (
  index: number,
  history: GroupedTrackingHistoryItem[],
  activeAccordion: string | null,
  groupSubtitle?: string,
) => {
  const prevGroup = history[index - 1];
  const isPreviousGroupExpanded = activeAccordion === prevGroup?.groupName + (index - 1);
  const isPreviousGroupCompleted =
    isCompleted(prevGroup?.history?.[prevGroup?.history?.length - 1]?.date) &&
    !prevGroup?.history?.[prevGroup?.history?.length - 1]?.isEstimatedDate;

  return classNames("grouped-tracking-history__line grouped-tracking-history__line--reverse", {
    "grouped-tracking-history__line--hidden": index === 0 || !isPreviousGroupExpanded,
    "grouped-tracking-history__line--reverse-extended": groupSubtitle, // Extend the line if there is a subtitle
    "grouped-tracking-history__line--completed": isPreviousGroupCompleted,
    "grouped-tracking-history__line--incompleted": !isCompleted(history[index]?.history[0]?.date),
    "grouped-tracking-history__line--upcoming":
      isPreviousGroupCompleted &&
      (!isCompleted(history[index]?.history[0]?.date) || history[index]?.history[0]?.isEstimatedDate),
  });
};

/**
 * Get the classes for the tracking history of the grouped tracking history item
 * @param index - index of the grouped tracking history item
 * @param trackingHistoryItems - array of tracking history items
 * @param history - array of grouped tracking history items
 * @param activeAccordion - the active accordion
 * @returns {string} - the classes for the tracking history of the grouped tracking history item
 */
const getTrackingHistoryClasses = (
  index: number,
  trackingHistoryItems: TrackingHistoryItemProps[],
  history: GroupedTrackingHistoryItem[],
  activeAccordion: string | null,
) => {
  const isGroupCompleted =
    isCompleted(trackingHistoryItems[trackingHistoryItems.length - 1].date) &&
    !trackingHistoryItems[trackingHistoryItems.length - 1].isEstimatedDate;
  const isExpanded = activeAccordion === history[index].groupName + index;
  const hideLineIconShadow =
    isGroupCompleted &&
    index + 1 < history.length && // Ensure there is a next group
    history[index + 1].history.length > 0 && // Ensure the next group has items
    new Date(history[index + 1].history[0].date) < new Date(); // Compare the date of the first item in the next group
  return classNames("grouped-tracking-history__history", {
    "grouped-tracking-history__history--hide-line-icon-shadow": hideLineIconShadow,
    "grouped-tracking-history__history--hide-line-icon": !isExpanded,
  });
};

/**
 * Get the classes for the marker icon of the grouped tracking history item
 * @param trackingHistoryItems - array of tracking history items
 * @returns {string} - the classes for the marker icon of the grouped tracking history item
 */
const getMarkerIconClasses = (trackingHistoryItems: TrackingHistoryItemProps[]) =>
  classNames("grouped-tracking-history__marker-icon", {
    "grouped-tracking-history__marker-icon--complete":
      isCompleted(trackingHistoryItems[0].date) && !trackingHistoryItems[0].isEstimatedDate,
    "grouped-tracking-history__marker-icon--incomplete":
      trackingHistoryItems[0].isEstimatedDate || !isCompleted(trackingHistoryItems[0].date),
  });

/**
 * Get all the classes for the grouped tracking history item
 * @param index - index of the grouped tracking history item
 * @param trackingHistoryItems - array of tracking history items
 * @param history - array of grouped tracking history items
 * @param activeAccordion - the active accordion
 * @returns {object} - the classes for the grouped tracking history item
 * @example {
 *              lineClasses: "grouped-tracking-history__line grouped-tracking-history__line--completed",
 *              reverseLineClasses: "grouped-tracking-history__line grouped-tracking-history__line--reverse",
 *              markerIconClasses: "grouped-tracking-history__marker-icon grouped-tracking-history__marker-icon--complete",
 *              trackingHistoryClasses: "grouped-tracking-history__history",
 *              isExpanded: true,
 *          }
 */
const getClasses = (
  index: number,
  trackingHistoryItems: TrackingHistoryItemProps[],
  history: GroupedTrackingHistoryItem[],
  activeAccordion: string | null,
) => {
  const isExpanded = activeAccordion === history[index].groupName + index;

  return {
    lineClasses: getLineClasses(index, trackingHistoryItems, history, activeAccordion),
    reverseLineClasses: getReverseLineClasses(index, history, activeAccordion, history[index].groupSubtitle),
    markerIconClasses: getMarkerIconClasses(trackingHistoryItems),
    trackingHistoryClasses: getTrackingHistoryClasses(index, trackingHistoryItems, history, activeAccordion),
    isExpanded,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { getClasses };
